import React, { useState, useMemo } from "react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useCompanyIncomeStatement } from "../hooks/useCompanyIncomeStatement";
import { useStockCurrency } from "../../../../contexts/StockCurrencyContext";
import { getChartColors } from "../constants/chartColors";
import { formatNumber } from "../utils/formatNumber";
import {
  transformFinancialData,
  calculateQuarterlyGrowthMetrics,
  calculateAnnualGrowthMetrics,
} from "../utils/chartDataTransformers";
import {
  ViewMode,
  DEFAULT_QUARTERLY_LIMIT,
  MAX_QUARTERLY_LIMIT,
  MAX_ANNUAL_LIMIT,
  safeSlice,
  getApiPeriodParam,
} from "./shared/financialPeriodConfig";
import GenericFinancialBarChart from "./shared/GenericFinancialBarChart";
import GrowthMetricsDisplay from "./shared/GrowthMetricsDisplay";
import ChartTooltip from "./shared/ChartTooltip";

interface EbitdaChartProps {
  ticker: string;
  height?: number;
  isModal?: boolean;
  onCloseModal?: () => void;
  renderCard?: boolean;
  showFullscreen?: boolean;
  viewMode?: ViewMode;
  limit?: number;
}

const EbitdaChart: React.FC<EbitdaChartProps> = ({
  ticker,
  height = 400,
  isModal = false,
  onCloseModal,
  renderCard = true,
  showFullscreen = false,
  viewMode: viewModeProp,
  limit: limitProp,
}) => {
  const theme = useTheme();
  const { currencySymbol } = useStockCurrency();
  const colors = getChartColors(theme.palette.mode === "dark");

  const [internalViewMode] = useState<ViewMode>("quarterly");
  const [internalLimit] = useState(DEFAULT_QUARTERLY_LIMIT);

  const effectiveViewMode = viewModeProp ?? internalViewMode;
  const effectiveLimit = limitProp ?? internalLimit;

  const period = getApiPeriodParam(effectiveViewMode);
  const maxLimit = effectiveViewMode === "quarterly" ? MAX_QUARTERLY_LIMIT : MAX_ANNUAL_LIMIT;
  const { data, isLoading, error } = useCompanyIncomeStatement(
    ticker,
    maxLimit,
    period
  );

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return safeSlice(
      transformFinancialData(
        data.filter((item) => item.ebitdaActual !== null),
        effectiveViewMode,
        "ebitda",
        (item) => item.ebitdaActual
      ),
      effectiveLimit
    );
  }, [data, effectiveViewMode, effectiveLimit]);

  const growthMetrics = useMemo(() => {
    if (chartData.length === 0) return [];
    return effectiveViewMode === "quarterly"
      ? calculateQuarterlyGrowthMetrics(chartData, "ebitda")
      : calculateAnnualGrowthMetrics(chartData, "ebitda", true);
  }, [chartData, effectiveViewMode]);

  return (
    <GenericFinancialBarChart
      title="EBITDA Growth"
      ticker={ticker}
      height={height}
      isModal={isModal}
      renderCard={renderCard}
      showFullscreen={showFullscreen}
      onCloseModal={onCloseModal}
      isLoading={isLoading}
      error={error}
      chartData={chartData}
      viewMode={effectiveViewMode}
      barKeys={["ebitda"]}
      colors={(bar: { data: Record<string, number> }) =>
        (bar.data.ebitda ?? 0) >= 0 ? colors.positive : colors.negative
      }
      yAxisLegend="EBITDA"
      yAxisFormat={(value) =>
        `${value >= 0 ? currencySymbol : "-" + currencySymbol}${formatNumber(Math.abs(value) * 1000000)}`
      }
      tooltipContent={({ indexValue, value, color }) => {
        const v = value as number;
        return (
          <ChartTooltip
            header={String(indexValue)}
            headerColor={color}
            rows={[{ label: "EBITDA", value: `${v >= 0 ? currencySymbol : "-" + currencySymbol}${formatNumber(Math.abs(v) * 1000000, 2)}` }]}
          />
        );
      }}
      growthMetrics={
        <GrowthMetricsDisplay metrics={growthMetrics} />
      }
    />
  );
};

export default EbitdaChart;
